import { AsyncPipe, NgComponentOutlet, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, ViewChild } from '@angular/core';
import { PActionButtonModule } from '@panaya/ui/p-action-button';
import { PIconModule } from '@panaya/ui/p-icon';
import { PActionButtonFloatingComponent } from '@panaya/ui/p-floating-action-button';
import { AiChatWindowMessageComponent } from './ai-chat-window-message';
import { AiChatWindowPromptBarComponent } from './ai-chat-window-prompt-bar';
import { StickToBottomDirective } from './stick-to-bottom.directive';
import { ThinkingEvent, AssistantMessage, ChatMessage } from '@panaya/ai-chat/shared';
import { injectPromptContextComposer, injectWidgetConfigProp } from '@panaya/ai-chat/utils';
import { WidgetResourceUrlPipe } from '../../pipes';
import { WritingIndicationComponent } from '../../writing-indication';
import { AiChatSuggestionsComponent } from './suggestions/ai-chat-suggestions.component';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { AiChatInternalPromptService, Suggestion } from '@panaya/ai-chat/domain';
import { NgLetModule } from 'ng-let';
import { AI_CHAT_LOG_FOOTER_COMPONENT } from '../../injection-tokens';

@Component({
    standalone: true,
    selector: 'p-ai-chat-window-messages',
    imports: [
        AiChatWindowPromptBarComponent,
        AiChatWindowMessageComponent,
        WritingIndicationComponent,
        StickToBottomDirective,
        WidgetResourceUrlPipe,
        PIconModule,
        PActionButtonModule,
        PActionButtonFloatingComponent,
        NgTemplateOutlet,
        NgComponentOutlet,
        NgIf,
        NgFor,
        AiChatSuggestionsComponent,
        TranslateModule,
        AsyncPipe,
        NgLetModule
    ],
    templateUrl: './ai-chat-window-messages.component.html',
    styleUrls: ['./ai-chat-window-messages.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatWindowMessagesComponent {
    /** The host's chat-log footer, such as the canvas draft card; `null` renders nothing. */
    protected readonly logFooterComponent = inject(AI_CHAT_LOG_FOOTER_COMPONENT);

    // Auto-follow / scroll-to-bottom behaviour lives in the directive on the scroll container.
    @ViewChild(StickToBottomDirective) private readonly stickToBottom?: StickToBottomDirective;

    @Input() public chatMessages: ChatMessage[] = [];
    @Input() public linkPrefix?: string;
    @Input() public assistantThinking = false;
    @Input() public assistantWorking = false;
    @Input() public assistantSilent = false;
    @Input() public showPromptBar = true;
    @Input() public suggestions?: Suggestion[] | null = [];
    @Input() public suggestionsFetchTrigger$?: Observable<void>;
    @Input() public thinkingEvent!: ThinkingEvent;

    private readonly composeContext = injectPromptContextComposer();
    private readonly internalPromptService = inject(AiChatInternalPromptService);

    protected readonly emptyStateImageURL = injectWidgetConfigProp('emptyStateImageURL');
    /** Whether to show the floating scroll-to-bottom button (driven by the directive). */
    protected showScrollToBottom = false;
    /**
     * Observable that detects when an internal prompt should show loading state
     */
    protected readonly shouldShowLoading$: Observable<boolean> = this.internalPromptService.shouldShowLoading$;

    /** The assistant has started answering but went silent: show the indication inside its message. */
    protected get showInlineIndication(): boolean {
        return this.assistantSilent && !this.assistantThinking;
    }

    protected isAiMessage(message: ChatMessage): message is AssistantMessage {
        return message.source === 'assistant';
    }

    protected scrollToLastMessage(): void {
        // A new prompt was just sent: resume following and bring it into view. Deferred
        // so the newly rendered user message is included in the scroll target.
        setTimeout(() => this.scrollToBottom(), 0);
    }

    protected scrollToBottom(): void {
        this.stickToBottom?.scrollToBottom();
    }

    protected onCanScrollDownChange(canScrollDown: boolean): void {
        this.showScrollToBottom = canScrollDown;
    }

    // Used for trackBy optimization
    protected messageIndex(index: number): number {
        return index;
    }

    protected onScroll(event: Event): void {
        // The title-bar shadow depends on how far the list is scrolled.
        const element = event.target as HTMLElement;
        element.classList.toggle('at-top', element.scrollTop === 0);
        element.classList.toggle('has-scroll', element.scrollHeight > element.clientHeight);
    }
}
